import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReasonField } from "@/components/ReasonField";
import { handleError } from "@/providers/errorHandler";

type ModalStep = "form" | "confirm" | "submitting" | "error";

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void>;
  title?: string;
  description?: string;
  confirmTitle?: string;
  confirmDescription?: string | React.ReactNode;
  submitLabel?: string;
  submitVariant?: "default" | "destructive" | "outline" | "ghost";
  doubleConfirm?: boolean;
  requireReason?: boolean;
  minReasonLength?: number;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonHint?: string;
  children?: React.ReactNode;
  footerNote?: string;
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && err.message.toLowerCase().includes("fetch");
}

export function ConfirmModal({
  open,
  onClose,
  onSubmit,
  title = "Подтвердить действие",
  confirmTitle,
  confirmDescription,
  submitLabel = "Подтвердить",
  doubleConfirm = false,
  requireReason = true,
  minReasonLength = 1,
  reasonLabel = "Причина",
  reasonPlaceholder = "Укажите причину...",
  reasonHint,
  children,
  footerNote = "Действие зафиксировано в Audit Log",
}: ConfirmModalProps): React.JSX.Element {
  const [step, setStep] = React.useState<ModalStep>("form");
  const [reason, setReason] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setStep("form");
      setReason("");
      setErrorMessage("");
    }
  }, [open]);

  const reasonValid = !requireReason || reason.length >= minReasonLength;

  async function handleFormSubmit(): Promise<void> {
    if (doubleConfirm) {
      setStep("confirm");
    } else {
      await doSubmit();
    }
  }

  async function doSubmit(): Promise<void> {
    setStep("submitting");
    setErrorMessage("");
    try {
      await onSubmit(reason);
      onClose();
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setErrorMessage("Нет соединения, повторите");
      } else {
        const bffErr = err as { message?: string; code?: string } | null;
        if (bffErr?.code) {
          const action = handleError({ code: bffErr.code, message: bffErr.message ?? "", traceId: "" });
          setErrorMessage("userMessage" in action ? action.userMessage : "Ошибка сервера. Попробуйте ещё раз.");
        } else {
          setErrorMessage("Ошибка сервера. Попробуйте ещё раз.");
        }
      }
      setStep("error");
    }
  }

  function handleCancel(): void {
    if (step === "confirm") {
      setStep("form");
    } else {
      onClose();
    }
  }

  const isSubmitting = step === "submitting";
  const showConfirmStep = step === "confirm";

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {showConfirmStep && confirmTitle ? confirmTitle : title}
          </DialogTitle>
        </DialogHeader>

        <div className="py-1 flex flex-col gap-3">
          {showConfirmStep ? (
            <>
              {confirmDescription && (
                <div className="bg-[var(--surface-2)] border border-[var(--border)] rounded-[var(--radius-sm)] p-3.5 text-[13px] text-[var(--fg)]">
                  {confirmDescription}
                </div>
              )}
              <div
                role="alert"
                className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.06)] border border-[rgba(196,58,42,0.18)] rounded-[var(--radius-sm)] text-[12px] text-[var(--fg)]"
              >
                <span className="text-[var(--danger)] flex-shrink-0">⚠</span>
                <span>
                  <strong>Это действие необратимо.</strong> Убедитесь в правильности введённых данных.
                </span>
              </div>
            </>
          ) : (
            <>
              {children}
              {requireReason && (
                <ReasonField
                  id="confirm-modal-reason"
                  label={reasonLabel}
                  value={reason}
                  onChange={setReason}
                  minLength={minReasonLength}
                  required
                  placeholder={reasonPlaceholder}
                  hint={reasonHint}
                />
              )}
              {(step === "error") && errorMessage && (
                <div
                  role="alert"
                  className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.08)] border border-[rgba(196,58,42,0.2)] rounded-[var(--radius-sm)] text-[12px] text-[var(--danger)]"
                >
                  <span>✕</span>
                  <span>{errorMessage}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border)]">
          <span className="text-[11px] text-[var(--muted)] flex-1 leading-relaxed pr-3">
            {footerNote}
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Отмена
            </Button>
            {showConfirmStep ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => { void doSubmit(); }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Загрузка..." : submitLabel}
              </Button>
            ) : (
              <Button
                type="button"
                variant="destructive"
                onClick={() => { void handleFormSubmit(); }}
                disabled={isSubmitting || !reasonValid}
              >
                {isSubmitting ? "Загрузка..." : (doubleConfirm ? "Далее" : submitLabel)}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
